/**
 * Notification Database Operations
 *
 * CRUD operations for in-app notifications.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { handleNotFoundError } from './helpers';

export type NotificationType =
  | 'mention'
  | 'assigned'
  | 'unassigned'
  | 'commented'
  | 'status_changed'
  | 'due_soon'
  | 'overdue'
  | 'sprint_started'
  | 'sprint_ended'
  | 'task_created'
  | 'task_completed';

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  taskId: string | null;
  sprintId: string | null;
  epicId: string | null;
  spaceId: string | null;
  triggeredBy: string | null;
  message: string | null;
  data: Record<string, unknown> | null;
  read: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface CreateNotificationData {
  userId: string;
  type: NotificationType;
  taskId?: string;
  sprintId?: string;
  epicId?: string;
  spaceId?: string;
  triggeredBy?: string;
  message?: string;
  data?: Record<string, unknown>;
}

export interface NotificationPreferences {
  id: string;
  userId: string;
  emailNotifications: boolean;
  pushNotifications: boolean;
  mentionNotifications: boolean;
  assignmentNotifications: boolean;
  commentNotifications: boolean;
  dueDateNotifications: boolean;
  statusChangeNotifications: boolean;
  sprintNotifications: boolean;
  digestFrequency: 'realtime' | 'hourly' | 'daily' | 'weekly' | 'never';
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  createdAt: string;
  updatedAt: string;
}

// Supabase row type for notifications table
interface SupabaseNotificationRow {
  id: string;
  user_id: string;
  type: NotificationType;
  task_id: string | null;
  sprint_id: string | null;
  epic_id: string | null;
  space_id: string | null;
  triggered_by: string | null;
  message: string | null;
  data: Record<string, unknown> | null;
  read: boolean;
  read_at: string | null;
  created_at: string;
}

// Supabase row type for notification_preferences table
interface SupabaseNotificationPreferencesRow {
  id: string;
  user_id: string;
  email_notifications: boolean;
  push_notifications: boolean;
  mention_notifications: boolean;
  assignment_notifications: boolean;
  comment_notifications: boolean;
  due_date_notifications: boolean;
  status_change_notifications: boolean;
  sprint_notifications: boolean;
  digest_frequency: 'realtime' | 'hourly' | 'daily' | 'weekly' | 'never';
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  created_at: string;
  updated_at: string;
}

// Convert from Supabase (snake_case) to Notification (camelCase)
function convertNotificationFromSupabase(row: SupabaseNotificationRow): Notification {
  return {
    id: row.id,
    userId: row.user_id,
    type: row.type,
    taskId: row.task_id,
    sprintId: row.sprint_id,
    epicId: row.epic_id,
    spaceId: row.space_id,
    triggeredBy: row.triggered_by,
    message: row.message,
    data: row.data,
    read: row.read,
    readAt: row.read_at,
    createdAt: row.created_at,
  };
}

function convertPreferencesFromSupabase(row: SupabaseNotificationPreferencesRow): NotificationPreferences {
  return {
    id: row.id,
    userId: row.user_id,
    emailNotifications: row.email_notifications,
    pushNotifications: row.push_notifications,
    mentionNotifications: row.mention_notifications,
    assignmentNotifications: row.assignment_notifications,
    commentNotifications: row.comment_notifications,
    dueDateNotifications: row.due_date_notifications,
    statusChangeNotifications: row.status_change_notifications,
    sprintNotifications: row.sprint_notifications,
    digestFrequency: row.digest_frequency,
    quietHoursStart: row.quiet_hours_start,
    quietHoursEnd: row.quiet_hours_end,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Get notifications for a user
 */
export async function getNotifications(
  userId: string,
  options: {
    limit?: number;
    offset?: number;
    unreadOnly?: boolean;
    types?: NotificationType[];
  } = {}
): Promise<Notification[]> {
  const { data, error } = await withRetry(() => {
    let query = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (options.unreadOnly) {
      query = query.eq('read', false);
    }

    if (options.types && options.types.length > 0) {
      query = query.in('type', options.types);
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    if (options.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
    }

    return query;
  });

  if (error) throw error;
  return (data || []).map(convertNotificationFromSupabase);
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(userId: string): Promise<number> {
  const { count, error } = await withRetry(() =>
    supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('read', false)
  );

  if (error) throw error;
  return count || 0;
}

/**
 * Create a notification (and trigger push delivery in background)
 */
export async function createNotification(data: CreateNotificationData): Promise<Notification> {
  const message = data.message || generateDefaultMessage(data);
  const insertData = {
    user_id: data.userId,
    type: data.type,
    task_id: data.taskId || null,
    sprint_id: data.sprintId || null,
    epic_id: data.epicId || null,
    space_id: data.spaceId || null,
    triggered_by: data.triggeredBy || null,
    message,
    data: data.data || null,
  };

  const { data: created, error } = await withRetry(() =>
    supabase
      .from('notifications')
      .insert(insertData)
      .select()
      .single()
  );

  if (error || !created) throw error || new Error('Failed to create notification');

  // Fire-and-forget: send push notification via Edge Function
  triggerPushNotification(data.userId, message, data.type, {
    taskId: data.taskId,
    notificationId: created.id,
  }).catch(() => {}); // never block on push failure

  return convertNotificationFromSupabase(created);
}

/**
 * Trigger push notification delivery via Edge Function.
 * Fire-and-forget — errors are silently ignored.
 */
async function triggerPushNotification(
  userId: string,
  body: string,
  type: string,
  data: Record<string, unknown>,
): Promise<void> {
  await supabase.functions.invoke('send-push-notification', {
    body: { userId, title: 'Zenda', body, type, data },
  });
}

/**
 * Bulk create notifications (e.g., for multiple assignees)
 */
export async function bulkCreateNotifications(
  notifications: CreateNotificationData[]
): Promise<Notification[]> {
  const insertData = notifications.map(data => ({
    user_id: data.userId,
    type: data.type,
    task_id: data.taskId || null,
    sprint_id: data.sprintId || null,
    epic_id: data.epicId || null,
    space_id: data.spaceId || null,
    triggered_by: data.triggeredBy || null,
    message: data.message || generateDefaultMessage(data),
    data: data.data || null,
  }));

  const { data: created, error } = await withRetry(() =>
    supabase
      .from('notifications')
      .insert(insertData)
      .select()
  );

  if (error) throw error;
  return (created || []).map(convertNotificationFromSupabase);
}

/**
 * Mark a notification as read
 */
export async function markAsRead(notificationId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('notifications')
      .update({ read: true, read_at: new Date().toISOString() })
      .eq('id', notificationId)
  );

  if (error) throw error;
}

/**
 * Mark multiple notifications as read
 */
export async function bulkMarkAsRead(notificationIds: string[]): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('notifications')
      .update({ read: true, read_at: new Date().toISOString() })
      .in('id', notificationIds)
  );

  if (error) throw error;
}

/**
 * Mark all notifications as read for a user
 */
export async function markAllAsRead(userId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('notifications')
      .update({ read: true, read_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('read', false)
  );

  if (error) throw error;
}

/**
 * Delete a notification
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)
  );

  if (error) throw error;
}

/**
 * Delete all read notifications for a user
 */
export async function deleteReadNotifications(userId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('notifications')
      .delete()
      .eq('user_id', userId)
      .eq('read', true)
  );

  if (error) throw error;
}

/**
 * Get notification preferences for a user
 */
export async function getNotificationPreferences(userId: string): Promise<NotificationPreferences | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', userId)
      .single()
  );

  if (error) return handleNotFoundError(error, null);
  return convertPreferencesFromSupabase(data);
}

/**
 * Update notification preferences
 */
export async function updateNotificationPreferences(
  userId: string,
  updates: Partial<Omit<NotificationPreferences, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>
): Promise<NotificationPreferences> {
  const updatePayload: Record<string, unknown> = {};

  if (updates.emailNotifications !== undefined) updatePayload.email_notifications = updates.emailNotifications;
  if (updates.pushNotifications !== undefined) updatePayload.push_notifications = updates.pushNotifications;
  if (updates.mentionNotifications !== undefined) updatePayload.mention_notifications = updates.mentionNotifications;
  if (updates.assignmentNotifications !== undefined) updatePayload.assignment_notifications = updates.assignmentNotifications;
  if (updates.commentNotifications !== undefined) updatePayload.comment_notifications = updates.commentNotifications;
  if (updates.dueDateNotifications !== undefined) updatePayload.due_date_notifications = updates.dueDateNotifications;
  if (updates.statusChangeNotifications !== undefined) updatePayload.status_change_notifications = updates.statusChangeNotifications;
  if (updates.sprintNotifications !== undefined) updatePayload.sprint_notifications = updates.sprintNotifications;
  if (updates.digestFrequency !== undefined) updatePayload.digest_frequency = updates.digestFrequency;
  if (updates.quietHoursStart !== undefined) updatePayload.quiet_hours_start = updates.quietHoursStart;
  if (updates.quietHoursEnd !== undefined) updatePayload.quiet_hours_end = updates.quietHoursEnd;

  // Upsert - create if doesn't exist
  const { data, error } = await withRetry(() =>
    supabase
      .from('notification_preferences')
      .upsert({ user_id: userId, ...updatePayload })
      .select()
      .single()
  );

  if (error || !data) throw error || new Error('Failed to update preferences');
  return convertPreferencesFromSupabase(data);
}

/**
 * Generate default notification message based on type
 */
function generateDefaultMessage(data: CreateNotificationData): string {
  switch (data.type) {
    case 'mention':
      return 'You were mentioned in a comment';
    case 'assigned':
      return 'You were assigned to a task';
    case 'unassigned':
      return 'You were removed from a task';
    case 'commented':
      return 'Someone commented on your task';
    case 'status_changed':
      return 'A task status was changed';
    case 'due_soon':
      return 'A task is due soon';
    case 'overdue':
      return 'A task is overdue';
    case 'sprint_started':
      return 'A sprint has started';
    case 'sprint_ended':
      return 'A sprint has ended';
    case 'task_created':
      return 'A new task was created';
    case 'task_completed':
      return 'A task was completed';
    default:
      return 'You have a new notification';
  }
}

// ============================================================================
// Helpers
// ============================================================================

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Filter out non-UUID assignee values (e.g. display names from seed data) */
function filterValidUserIds(ids: string[]): string[] {
  return ids.filter(id => UUID_RE.test(id));
}

// ============================================================================
// Notification Triggers (to be called from other modules)
// ============================================================================

/**
 * Notify users when they are mentioned in a comment
 */
export async function notifyMention(
  mentionedUserIds: string[],
  taskId: string,
  triggeredBy: string,
  commentContent: string
): Promise<void> {
  const validIds = filterValidUserIds(mentionedUserIds);
  if (validIds.length === 0) return;

  const notifications = validIds
    .filter(userId => userId !== triggeredBy) // Don't notify yourself
    .map(userId => ({
      userId,
      type: 'mention' as NotificationType,
      taskId,
      triggeredBy,
      message: `You were mentioned in a comment`,
      data: { commentPreview: commentContent.substring(0, 100) },
    }));

  if (notifications.length > 0) {
    await bulkCreateNotifications(notifications);
  }
}

/**
 * Notify users when they are assigned to a task
 */
export async function notifyAssignment(
  assignedUserIds: string[],
  taskId: string,
  taskName: string,
  triggeredBy: string
): Promise<void> {
  const validIds = filterValidUserIds(assignedUserIds);
  if (validIds.length === 0) return;

  const notifications = validIds
    .filter(userId => userId !== triggeredBy)
    .map(userId => ({
      userId,
      type: 'assigned' as NotificationType,
      taskId,
      triggeredBy,
      message: `You were assigned to "${taskName}"`,
    }));

  if (notifications.length > 0) {
    await bulkCreateNotifications(notifications);
  }
}

/**
 * Notify task assignees when status changes
 */
export async function notifyStatusChange(
  assigneeIds: string[],
  taskId: string,
  taskName: string,
  oldStatus: string,
  newStatus: string,
  triggeredBy: string
): Promise<void> {
  const validIds = filterValidUserIds(assigneeIds);
  if (validIds.length === 0) return;

  const notifications = validIds
    .filter(userId => userId !== triggeredBy)
    .map(userId => ({
      userId,
      type: 'status_changed' as NotificationType,
      taskId,
      triggeredBy,
      message: `"${taskName}" status changed from ${oldStatus} to ${newStatus}`,
      data: { oldStatus, newStatus },
    }));

  if (notifications.length > 0) {
    await bulkCreateNotifications(notifications);
  }
}

/**
 * Notify task assignees when someone comments
 */
export async function notifyComment(
  assigneeIds: string[],
  taskId: string,
  taskName: string,
  triggeredBy: string,
  commentPreview: string
): Promise<void> {
  const validIds = filterValidUserIds(assigneeIds);
  if (validIds.length === 0) return;

  const notifications = validIds
    .filter(userId => userId !== triggeredBy)
    .map(userId => ({
      userId,
      type: 'commented' as NotificationType,
      taskId,
      triggeredBy,
      message: `New comment on "${taskName}"`,
      data: { commentPreview: commentPreview.substring(0, 100) },
    }));

  if (notifications.length > 0) {
    await bulkCreateNotifications(notifications);
  }
}
